import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, Button, Col, Form, Input, Row, Select, Space, Tag } from 'antd'
import { Plus } from 'lucide-react'
import type { CustomerUpsertInput } from '@super-mega/server/contracts/customer'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { useTRPC } from '@/trpc/react.ts'
import { CustomerTagEditorModal, type CustomerTagEditorRecord } from './customer-tag-editor-modal.tsx'

export function CustomerBasicFields({
  salespersonOptions,
  salespeopleLoading,
  currentTagOptions = []
}: {
  salespersonOptions: Array<{ label: string; value: number }>
  salespeopleLoading: boolean
  currentTagOptions?: CustomerTagEditorRecord[]
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const form = Form.useFormInstance<CustomerUpsertInput>()
  const [creatingTag, setCreatingTag] = useState(false)
  const tags = useQuery({
    ...trpc.lookup.customerTags.queryOptions({ purpose: 'customer.read' }),
    staleTime: 60_000
  })
  const tagSelectOptions = [...(tags.data ?? []), ...currentTagOptions].reduce<
    Array<{ label: string; value: number; color: string }>
  >((options, tag) => {
    if (options.some((option) => option.value === tag.id)) {
      return options
    }
    options.push({ color: tag.color, label: tag.name, value: tag.id })
    return options
  }, [])

  const handleCustomerTagSaved = async (tag: CustomerTagEditorRecord) => {
    await Promise.all([
      queryClient.invalidateQueries(trpc.customer.tagList.queryFilter()),
      queryClient.invalidateQueries(trpc.lookup.customerTags.queryFilter())
    ])
    const selectedTagIds = (form.getFieldValue('tagIds') as number[] | undefined) ?? []
    if (!selectedTagIds.includes(tag.id)) {
      form.setFieldValue('tagIds', [...selectedTagIds, tag.id])
    }
    setCreatingTag(false)
  }

  return (
    <>
      {tags.isError && (
        <Alert
          className="mb-4"
          type="error"
          showIcon
          title="客户标签加载失败"
          action={<Button onClick={() => void tags.refetch()}>重试</Button>}
        />
      )}
      <Row gutter={16}>
        <Col sm={12} xs={24}>
          <Form.Item
            label="客户名称"
            name="name"
            rules={[{ max: 255, required: true, whitespace: true, message: '请输入客户名称（最多 255 字）' }]}
          >
            <Input placeholder="请输入客户名称" />
          </Form.Item>
        </Col>
        <Col sm={12} xs={24}>
          <Form.Item label="客户类型" name="type" rules={[{ required: true }]}>
            <Select
              options={[
                { label: '普通客户', value: 'normal' },
                { label: '代理商客户', value: 'agent' }
              ]}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col sm={12} xs={24}>
          <Form.Item label="业务员" name="salespersonId">
            <Select
              allowClear
              loading={salespeopleLoading}
              options={salespersonOptions}
              placeholder="请选择一位业务员"
              showSearch={{ optionFilterProp: 'label' }}
            />
          </Form.Item>
        </Col>
        <Col sm={12} xs={24}>
          <Form.Item label="客户企业信息" name="companyInfo" rules={[{ max: 255 }]}>
            <Input placeholder="请输入客户所属企业名称" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label="客户标签">
        <div className="flex flex-col gap-2">
          <Space.Compact block>
            <Form.Item name="tagIds" noStyle rules={[{ type: 'array', max: 50, message: '最多选择 50 个客户标签' }]}>
              <Select
                showSearch={{ optionFilterProp: 'label' }}
                allowClear
                className="min-w-0 flex-1"
                loading={tags.isLoading || tags.isFetching}
                mode="multiple"
                options={tagSelectOptions}
                placeholder="请选择客户标签"
                classNames={{ content: 'gap-1' }}
                tagRender={({ label, value }) => {
                  const tag = tagSelectOptions.find((option) => option.value === value)
                  return <Tag color={tag?.color}>{label}</Tag>
                }}
              />
            </Form.Item>
            <Authorized permissions={['customer.tag.create']}>
              <Button icon={<Plus aria-hidden size={16} />} onClick={() => setCreatingTag(true)}>
                新建标签
              </Button>
            </Authorized>
          </Space.Compact>
        </div>
      </Form.Item>
      <CustomerTagEditorModal
        editing={creatingTag ? 'new' : undefined}
        onClose={() => setCreatingTag(false)}
        onSaved={handleCustomerTagSaved}
      />
    </>
  )
}
